import React from "react";
import { useApp } from "@/state/AppContext";
import { Button } from "@/components/ui/button";

/** A widget throwing must never take down the rest of the page — only class components can
    catch render errors (componentDidCatch / getDerivedStateFromError has no hook equivalent),
    so the catching logic lives here and reads its copy via props from the functional wrapper
    below. `resetKey` remounts the subtree on retry so a widget that fetches on mount actually
    retries the fetch, not just re-renders stale state. */
class ErrorBoundaryImpl extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, resetKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught an error:", error, info);
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          data-testid="error-boundary"
        >
          {this.props.message}
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            data-testid="error-boundary-retry"
            onClick={this.reset}
          >
            {this.props.retryLabel}
          </Button>
        </div>
      );
    }
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

export function ErrorBoundary({ children }) {
  const { t } = useApp();
  return (
    <ErrorBoundaryImpl message={t("errors.generic")} retryLabel={t("common.retry")}>
      {children}
    </ErrorBoundaryImpl>
  );
}
